import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, Loader2, ArrowRight, Check, Video } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface LivestreamRegistrationFormProps {
  onSuccess: () => void;
}

const ATTENDEE_TYPES = [
  "Streaming Attendee",
  "Coach – Remotely",
];

const STEPS = [
  { number: 1, title: "Basic Info" },
  { number: 2, title: "Details" },
];

const LivestreamRegistrationForm: React.FC<LivestreamRegistrationFormProps> = ({ onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    attendeeType: "",
    hopeToGet: "",
    personalizationOptIn: false,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Helper to determine is_in_person from attendee_type
  const determineIsInPerson = (attendeeType: string): boolean => {
    if (!attendeeType) return false; // Livestream defaults to false
    const remoteKeywords = ["remote", "stream", "virtual", "online"];
    const lowerType = attendeeType.toLowerCase();
    return !remoteKeywords.some(keyword => lowerType.includes(keyword));
  };

  // Build the structured form_fields object
  const buildFormFields = (currentFormData: typeof formData, recordId: string) => {
    return {
      record_id: recordId,
      form_fields: {
        first_name: currentFormData.firstName,
        last_name: currentFormData.lastName,
        email: currentFormData.email,
        attendee_type: currentFormData.attendeeType || null,
        is_in_person: determineIsInPerson(currentFormData.attendeeType),
        interest_text_raw: currentFormData.hopeToGet || null,
        personalization_opt_in: currentFormData.personalizationOptIn,
      },
    };
  };

  // Step 1: INSERT new row
  const handleStep1Submit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from("event_registrations")
        .insert({
          registration_source: "livestream_form",
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          form_fields: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            is_in_person: false,
          },
        })
        .select("id")
        .single();

      if (error) throw error;
      
      setRegistrationId(data.id);
      setCurrentStep(2);
      toast.success("Progress saved!");
    } catch (error: any) {
      console.error("Step 1 error:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: UPDATE existing row (final)
  const handleStep2Submit = async () => {
    if (!registrationId) {
      toast.error("Session error. Please start over.");
      return;
    }

    if (!formData.attendeeType || !formData.hopeToGet) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      const finalFormFields = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        attendee_type: formData.attendeeType,
        is_in_person: determineIsInPerson(formData.attendeeType),
        interest_text_raw: formData.hopeToGet || null,
        personalization_opt_in: formData.personalizationOptIn,
      };

      const { error } = await supabase
        .from("event_registrations")
        .update({
          attendee_type: formData.attendeeType,
          interest_text_raw: formData.hopeToGet,
          personalization_opt_in: formData.personalizationOptIn,
          form_fields: finalFormFields,
        })
        .eq("id", registrationId);

      if (error) throw error;
      
      toast.success("Registration complete!");
      onSuccess();
    } catch (error: any) {
      console.error("Step 2 error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep === 1) handleStep1Submit();
    else if (currentStep === 2) handleStep2Submit();
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      {/* Step Indicator */}
      <div className="flex items-center justify-center mb-6">
        {STEPS.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300",
                  currentStep > step.number
                    ? "bg-secondary text-secondary-foreground"
                    : currentStep === step.number
                    ? "bg-secondary text-secondary-foreground ring-4 ring-secondary/20"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {currentStep > step.number ? (
                  <Check className="w-4 h-4" />
                ) : (
                  step.number
                )}
              </div>
              <span
                className={cn(
                  "text-xs mt-1.5 font-medium transition-colors",
                  currentStep >= step.number ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={cn(
                  "w-16 sm:w-24 h-0.5 mx-2 transition-colors duration-300",
                  currentStep > step.number ? "bg-secondary" : "bg-muted"
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Form Card */}
      <div className="bg-card border border-border rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-secondary/20 rounded-lg">
            <Video className="w-4 h-4 text-secondary" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Livestream Registration</span>
        </div>

        <form onSubmit={handleStepSubmit}>
          {/* Step 1: Basic Info */}
          <div
            className={cn(
              "transition-all duration-300",
              currentStep === 1 ? "opacity-100" : "hidden opacity-0"
            )}
          >
            <h3 className="text-lg font-semibold text-foreground mb-1">Let's get started</h3>
            <p className="text-sm text-muted-foreground mb-5">Tell us who you are</p>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ls-firstName" className="text-sm">First Name *</Label>
                  <Input
                    id="ls-firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    className="h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ls-lastName" className="text-sm">Last Name *</Label>
                  <Input
                    id="ls-lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className="h-10"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ls-email" className="text-sm">Email *</Label>
                <Input
                  id="ls-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john.doe@example.com"
                  className="h-10"
                />
              </div>
            </div>
          </div>

          {/* Step 2: Details */}
          <div
            className={cn(
              "transition-all duration-300",
              currentStep === 2 ? "opacity-100" : "hidden opacity-0"
            )}
          >
            <h3 className="text-lg font-semibold text-foreground mb-1">Almost there!</h3>
            <p className="text-sm text-muted-foreground mb-5">Tell us about your goals</p>
            
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ls-attendeeType" className="text-sm">How are you joining? *</Label>
                <Select
                  value={formData.attendeeType}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, attendeeType: value }))
                  }
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ATTENDEE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ls-hopeToGet" className="text-sm">What do you hope to get out of the event? *</Label>
                <Textarea
                  id="ls-hopeToGet"
                  name="hopeToGet"
                  value={formData.hopeToGet}
                  onChange={handleInputChange}
                  placeholder="Share your goals..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                <Checkbox
                  id="ls-personalizationOptIn"
                  checked={formData.personalizationOptIn}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      personalizationOptIn: checked === true,
                    }))
                  }
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="ls-personalizationOptIn" className="cursor-pointer text-sm font-medium">
                      Personalize my experience
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            Our goal is to show how AI can support real human connection. If you allow it, 
                            we'll see if it's possible to use publicly available social information (like a 
                            profile photo or bio) to personalize your experience and help our team recognize 
                            and support you better. We never use private or sensitive data, and you may opt 
                            out at any time.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full mt-6 h-11"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : currentStep === 2 ? (
              "Complete Registration"
            ) : (
              <>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LivestreamRegistrationForm;
